import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../utils/permissions";
import { apiRequest } from "../../services/api";
const field = "mt-1 block w-full rounded-lg border p-2",
  button = "rounded-lg border px-3 py-2 disabled:opacity-40";
export default function PaymentDebitReview({ companyId, debit, onUpdated }) {
  const { accessToken, user } = useAuth(),
    canReview = hasPermission(user, "accounting.vouchers_reverse");
  const [action, setAction] = useState("VOID"),
    [credits, setCredits] = useState([]),
    [preview, setPreview] = useState(null),
    [confirmed, setConfirmed] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const generation = useRef(0),
    working = useRef(false),
    retry = useRef(null);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  const root = `/accounting/${companyId}/payment-debits`;
  async function run(fn, done) {
    if (working.current) return;
    working.current = true;
    const g = ++generation.current;
    setBusy(true);
    setError("");
    try {
      const r = await fn();
      if (g === generation.current) done(r);
    } catch (e) {
      if (g === generation.current) setError(e.message);
    } finally {
      if (g === generation.current) {
        working.current = false;
        setBusy(false);
      }
    }
  }
  const request = (url, options = {}) =>
    apiRequest(url, { accessToken, ...options });
  function clear() {
    setPreview(null);
    setConfirmed(false);
  }
  function save() {
    if (!preview || !confirmed || !canReview || busy) return;
    const body = {
        ...preview.command,
        previewHash: preview.previewHash,
        correctionReviewed: true,
        evidenceOnlyAccepted: true,
      },
      signature = JSON.stringify(body);
    if (retry.current?.signature !== signature) {
      if (!window.crypto?.randomUUID) {
        setError("HTTPS or localhost is required for secure review keys");
        return;
      }
      retry.current = { signature, key: window.crypto.randomUUID() };
    }
    body.requestKey = retry.current.key;
    run(async () => {
      await request(`${root}/${debit.id}/review`, { method: "POST", body });
      await onUpdated();
    }, clear);
  }
  return (
    <details className="min-w-72 space-y-3">
      <summary className="cursor-pointer">Review / retained history</summary>
      <p className="mt-3">
        Original: {debit.kind}. Current: {debit.currentKind || debit.kind}.{" "}
        {debit.voidedId
          ? "Voided evidence."
          : `Active evidence. Recovery retained: INR ${debit.recovered || "0.00"}.`}
      </p>
      <ol className="space-y-2">
        {(debit.events || []).map((e) => (
          <li key={e.id} className="rounded border p-2">
            #{e.sequence} · {e.kind}
            {e.classification ? ` → ${e.classification}` : ""}
            {e.kind === "RECOVERY"
              ? ` · INR ${e.amount} · ${e.externalReference}${e.voidedId ? " · voided" : ""}`
              : ""}
            <p>{e.reason}</p>
            <small>
              {e.createdAt} · {e.createdBy}
            </small>
          </li>
        ))}
      </ol>
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {canReview && !debit.voidedId && (
        <fieldset disabled={busy} className="mt-3 space-y-3 rounded border p-3">
          <label className="block">
            Review action
            <select
              className={field}
              value={action}
              onChange={(e) => {
                clear();
                setAction(e.target.value);
                setCredits([]);
              }}
            >
              <option value="VOID">Void mistaken debit evidence</option>
              <option value="RECLASSIFY">Correct classification</option>
              <option value="RECOVERY">
                Record chargeback recovery credit
              </option>
              <option value="VOID_RECOVERY">
                Void mistaken recovery evidence
              </option>
            </select>
          </label>
          {action === "RECOVERY" && (
            <button
              type="button"
              className={button}
              onClick={() =>
                run(
                  () =>
                    request(
                      `${root}/bank-credits?channelId=${encodeURIComponent(debit.channelId)}`,
                    ),
                  (r) => setCredits(r.rows),
                )
              }
            >
              Load retained recovery credits
            </button>
          )}
          <form
            key={action}
            className="space-y-3"
            onChange={clear}
            onSubmit={(e) => {
              e.preventDefault();
              const command = {
                ...Object.fromEntries(new FormData(e.currentTarget)),
                kind: action,
              };
              run(
                () =>
                  request(`${root}/${debit.id}/review-preview`, {
                    method: "POST",
                    body: command,
                  }),
                (r) => setPreview({ ...r, command }),
              );
            }}
          >
            {action === "RECLASSIFY" && (
              <label className="block">
                Corrected classification
                <select className={field} name="classification">
                  <option value="REFUND_DEBIT">Customer refund debit</option>
                  <option value="CHARGEBACK_DEBIT">Chargeback debit</option>
                </select>
              </label>
            )}
            {action === "RECOVERY" && (
              <>
                <label className="block">
                  Exact recovery bank credit
                  <select className={field} required name="bankRowId">
                    <option value="">Select credit (latest 100 shown)</option>
                    {credits.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.date.slice(0, 10)} · INR {r.credit} ·{" "}
                        {r.reference || r.description}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  Recovery amount (INR)
                  <input
                    className={field}
                    name="amount"
                    required
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]{1,2})?"
                  />
                </label>
                <label className="block">
                  Recovery transaction reference
                  <input
                    className={field}
                    required
                    name="externalReference"
                    minLength={3}
                    maxLength={150}
                  />
                </label>
              </>
            )}
            {action === "VOID_RECOVERY" && (
              <label className="block">
                Recovery to void
                <select className={field} name="recoveryId" required>
                  <option value="">Select active recovery</option>
                  {(debit.events || [])
                    .filter((e) => e.kind === "RECOVERY" && !e.voidedId)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        #{e.sequence} · INR {e.amount} · {e.externalReference}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <label className="block">
              Correction / recovery reason
              <textarea
                className={field}
                required
                name="reason"
                minLength={20}
                maxLength={1000}
              />
            </label>
            <button className={button}>Preview evidence review</button>
          </form>
          {preview && (
            <div className="space-y-3 rounded bg-amber-50 p-3">
              <p>{preview.warning}</p>
              <p>
                Current classification: {preview.payload.currentKind}. Active
                recoveries: INR {preview.payload.recovered}. Recovery capacity
                before this event: INR {preview.payload.recoveryRemaining}.
              </p>
              <label className="block">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />{" "}
                I reviewed this correction/recovery and accept that it changes
                retained evidence only, not financial books or money movement.
              </label>
              <button className={button} disabled={!confirmed} onClick={save}>
                Retain reviewed evidence event
              </button>
            </div>
          )}
        </fieldset>
      )}
    </details>
  );
}
